import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/lib/useSiteSettings";

const navLinks = [
  { label: "Inicio", path: "/" },
  { label: "Noticias", path: "/noticias" },
  { label: "Actividades", path: "/actividades" },
  { label: "Comunicados", path: "/comunicados" },
  { label: "Sobre el APR", path: "/sobre-el-apr" },
  { label: "Contacto", path: "/contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const siteConfig = useSiteSettings();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "bg-white shadow-sm border-b border-sky-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-500 text-white">
            <i className="ri-drop-fill text-xl"></i>
          </div>
          <div>
            <span
              className={`font-bold text-base md:text-lg leading-tight block transition-colors duration-300 ${
                scrolled || !isHome ? "text-sky-900" : "text-white"
              }`}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {siteConfig.aprName}
            </span>
            <span
              className={`text-xs leading-tight block transition-colors duration-300 ${
                scrolled || !isHome ? "text-sky-500" : "text-sky-200"
              }`}
            >
              Agua Potable Rural
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === link.path
                  ? scrolled || !isHome
                    ? "text-sky-600 bg-sky-50"
                    : "text-white bg-white/20"
                  : scrolled || !isHome
                  ? "text-slate-600 hover:text-sky-600 hover:bg-sky-50"
                  : "text-white/90 hover:text-white hover:bg-white/20"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href={siteConfig.portalUrl || "http://localhost:15174"}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all duration-200 whitespace-nowrap cursor-pointer ${
              scrolled || !isHome
                ? "border-sky-500 text-sky-600 hover:bg-sky-50"
                : "border-white text-white hover:bg-white/20"
            }`}
          >
            <i className="ri-user-line text-base"></i>
            Portal usuarios
          </a>
          <Link
            to="/pagar"
            className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold bg-sky-500 text-white hover:bg-sky-600 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-sm"
          >
            <i className="ri-bank-card-line text-base"></i>
            Pagar cuenta
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
            scrolled || !isHome ? "text-sky-900" : "text-white"
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Abrir menú"
        >
          <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-sky-100 shadow-lg">
          <nav className="flex flex-col px-4 py-4 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                  location.pathname === link.path
                    ? "text-sky-600 bg-sky-50"
                    : "text-slate-700 hover:text-sky-600 hover:bg-sky-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-sky-100">
              <a
                href={siteConfig.portalUrl || "http://localhost:15174"}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold border-2 border-sky-500 text-sky-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-line"></i>
                Portal usuarios
              </a>
              <Link
                to="/pagar"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold bg-sky-500 text-white cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bank-card-line"></i>
                Pagar cuenta
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
